import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/resources", label: "Resources" },
  { to: "/overdose-awareness-license-plates", label: "Plate" },
  { to: "/connect-with-us", label: "Connect" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[var(--rts-blue-light)]" data-testid="site-header">
      {/* Top utility bar */}
      <div className="bg-[var(--rts-blue-deep)] text-white text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="font-medium tracking-wide" data-testid="hashtag-text">#ReversingTheStigma</span>
          <div className="flex items-center gap-5">
            <a href="mailto:ReversingStigma@gmail.com" className="flex items-center gap-2 hover:text-[var(--rts-blue-light)] transition" data-testid="header-email">
              <Mail className="w-3.5 h-3.5" /> ReversingStigma@gmail.com
            </a>
            <a href="https://www.facebook.com/PARSProjectAddictionReversingtheStigma/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[var(--rts-blue-light)]" data-testid="header-facebook">
              <i className="fa-brands fa-facebook-f" />
            </a>
            <a href="https://www.instagram.com/p._a._r._s/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[var(--rts-blue-light)]" data-testid="header-instagram">
              <i className="fa-brands fa-instagram" />
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img src="/logo.jpg" alt="PARS - Project Addiction: Reversing the Stigma logo" className="h-16 w-auto object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="text-[var(--rts-blue-deep)] font-display text-lg font-bold">Reversing the Stigma</div>
            <div className="text-xs text-[var(--rts-muted)] tracking-wider uppercase">Project Addiction · PARS</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-[var(--rts-blue)] text-white"
                    : "text-[var(--rts-ink)] hover:bg-[var(--rts-blue-pale)] hover:text-[var(--rts-blue-deep)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/connect-with-us"
            className="ml-3 inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-red-200 transition-all hover:-translate-y-0.5"
            data-testid="nav-donate-cta"
          >
            Get Involved
          </Link>
        </nav>

        <button
          className="lg:hidden p-2 rounded-md text-[var(--rts-blue-deep)]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[var(--rts-blue-light)] bg-white overflow-hidden animate-slide-down" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-base font-semibold ${
                    isActive
                      ? "bg-[var(--rts-blue)] text-white"
                      : "text-[var(--rts-ink)] hover:bg-[var(--rts-blue-pale)]"
                  }`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <a href="mailto:ReversingStigma@gmail.com" className="block px-4 py-3 text-sm text-[var(--rts-muted)]">
              ReversingStigma@gmail.com
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
